import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import CheckIcon from "@mui/icons-material/Check";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import type { DateTime } from "luxon";
import { PageHeader } from "@/components/PageHeader";
import { useSnapshot } from "./snapshot.context";
import { todayInSydney } from "./snapshot.utils";

interface SnapshotHeaderProps {
  title: string;
  children?: ReactNode;
}

/**
 * A Snapshot page's header: the title, the day (stepped with ◀ ▶ or picked), the children (extra
 * buttons, which can read the day with useSnapshot) and "Build all".
 */
export const SnapshotHeader = (props: SnapshotHeaderProps) => {
  const { title, children } = props;
  const snapshot = useSnapshot();
  const day = snapshot.day;
  const isToday = day.hasSame(todayInSydney(), "day");
  const pendingCount = snapshot.pendingReports.length;

  let buildAllIcon = <LayersOutlinedIcon fontSize="small" />;
  let buildAllLabel = `Build all (${pendingCount})`;
  if (snapshot.isAnyBuilding) {
    buildAllIcon = (
      <CircularProgress
        color="inherit"
        size={14}
        thickness={6}
      />
    );
    buildAllLabel = "Building…";
  }
  else if (pendingCount === 0) {
    buildAllIcon = <CheckIcon fontSize="small" />;
    buildAllLabel = "All built";
  }

  const handleDayChange = (value: DateTime | null) => {
    if (value) snapshot.setDay(value);
  };

  const handlePreviousDay = () => {
    snapshot.setDay(day.minus({ days: 1 }));
  };

  const handleNextDay = () => {
    if (!isToday) snapshot.setDay(day.plus({ days: 1 }));
  };

  return (
    <Box
      sx={{
        overflow: "hidden",
        scrollbarGutter: "stable",
      }}
    >
      <PageHeader title={title}>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
          }}
        >
          <IconButton
            aria-label="Previous day"
            disabled={snapshot.isUploading}
            onClick={handlePreviousDay}
            size="small"
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <DatePicker
            disabled={snapshot.isUploading}
            disableFuture
            format="ccc d MMMM yyyy"
            onChange={handleDayChange}
            slotProps={{
              openPickerButton: { size: "small" },
              openPickerIcon: { fontSize: "small" },
              textField: { size: "small", variant: "standard", sx: { width: 200, mx: 1 } },
            }}
            value={day}
          />
          <IconButton
            aria-label="Next day"
            disabled={snapshot.isUploading || isToday}
            onClick={handleNextDay}
            size="small"
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Stack>
        {children}
        <Button
          disabled={snapshot.isAnyBuilding || pendingCount === 0}
          onClick={snapshot.buildAll}
          startIcon={buildAllIcon}
          variant="contained"
          size="small"
        >
          {buildAllLabel}
        </Button>
      </PageHeader>
    </Box>
  );
};
